import React, {useState, useEffect, useCallback} from 'react';
import {v4 as uuidv4} from 'uuid';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import useDeepCompareEffect from 'use-deep-compare-effect';

import ExerciseAppBar from '../../components/ExerciseAppBar';
import Instructions from './Instructions';
import ProductsOverlay from './ProductsOverlay';
import Designer from './Designer';
import {CHAR_BANK_ID, CHAR_BANK_2_ID, DEFAULT_WHITEBOARD} from '../constants';
import SecondaryAppBar from '../../components/SecondaryAppBar';
import Card from '../../components/Card';
import CardControlled from './CardControlled';

import banners from '../data/whiteboards';
import letters from '../data/letters';
import letterCombinations from '../data/letterCombinations';
import shapes from '../data/shapes';
import clipart from '../data/clipart';

import FlamingoLiteracyLogoDark from '../../components/icons/FlamingoLiteracyLogoDark';
import trashSound from '../sounds/paper-crush.mp3';

import baseStyles from '../../css/Base.module.css';
import '../css/fonts.css';
import '../css/app.css';

const defaultState = Object.freeze({
  activeView: 'DESIGNER',
  boxes: {
    [CHAR_BANK_ID]: {
      uuid: CHAR_BANK_ID,
      id: CHAR_BANK_ID,
      items: [],
    },
    [CHAR_BANK_2_ID]: {
      uuid: CHAR_BANK_2_ID,
      id: CHAR_BANK_2_ID,
      items: [],
    },
  },
  card: {
    id: null,
    position: {
      x: null,
      y: null,
    },
    size: {
      width: null,
      height: null,
    },
    relativePosition: {
      x: null,
      y: null,
    },
    relativeSize: {
      width: null,
      height: null,
    },
  },
});

export default function WordWorkApp({exercise, handleSave, mode, isEditable}) {
  const [activeView, setActiveView] = useState(defaultState.activeView); // INSTRUCTIONS, DESIGNER
  const [isProductsOverlayVisible, setIsProductsOverlayVisible] = useState(false);
  const [banner, setBanner] = useState(DEFAULT_WHITEBOARD);
  const [clipartID, setClipartID] = useState(null);
  const [boxes, setBoxes] = useState(defaultState.boxes);
  const [boxOrder, setBoxOrder] = useState([]);
  const [isCardVisible, setIsCardVisible] = useState(false); // temporary blend as you go card
  const [card, setCard] = useState(defaultState.card); // Saved blend as you go card

  // When the window size changes, calculate the card postion and size in pixels based on the saved relative values
  // Relative position and size is relative to the designer banner container
  const handleCardContainerResize = useCallback(() => {
    // Only if card is visible
    if (card?.id) {
      const container = document.getElementsByClassName('designer-banner-container')[0].getBoundingClientRect();

      const desiredXOffsetPercent = card.relativePosition.x;
      const desiredYOffsetPercent = card.relativePosition.y;
      const cardX = container.width * desiredXOffsetPercent + container.left;
      let cardY = container.height * desiredYOffsetPercent + container.top;
      if (mode === 'LESSON') cardY = cardY - 48; // Subtract height of primary lesson header, which does not impact card's y position

      const desiredHeightPercent = card.relativeSize.height;
      const desiredWidthPercent = card.relativeSize.width;
      const cardHeight = container.height * desiredHeightPercent;
      const cardWidth = container.width * desiredWidthPercent;

      setCard({
        ...card,
        position: {
          x: cardX,
          y: cardY,
        },
        size: {
          width: cardWidth,
          height: cardHeight,
        },
      });
    }
  }, [card, mode]);

  const loadStateFromConfig = useCallback(() => {
    const hasConfig = Object.keys(exercise?.config ?? {}).length > 0;

    if (hasConfig) {
      const config = JSON.parse(JSON.stringify(exercise.config));
      const allData = {
        ...banners,
        ...letters,
        ...letterCombinations,
        ...shapes,
        ...clipart,
      };

      Object.keys(config.boxes).forEach((key) => {
        const id = config.boxes[key].id;

        config.boxes[key] = {
          ...allData[id],
          uuid: key === CHAR_BANK_ID ? CHAR_BANK_ID : key,
          items: config.boxes[key].items.map((itemID) => ({
            ...allData[itemID],
            uuid: uuidv4(),
          })),
        };
      });

      setActiveView(config.activeView);
      setBanner(config.banner);
      setClipartID(config.clipartID);
      setBoxes(config.boxes);
      setBoxOrder(config.boxOrder);
      setCard(config.card);
    }
  }, [exercise?.config]);

  useEffect(() => {
    loadStateFromConfig();
  }, [exercise._id, loadStateFromConfig]);

  useDeepCompareEffect(() => {
    handleCardContainerResize();
  }, [card]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleClickSave() {
    handleSave(generateConfig());
  }

  function generateConfig() {
    const boxesClone = JSON.parse(JSON.stringify(boxes));
    Object.keys(boxesClone).forEach((boxID) => {
      boxesClone[boxID] = {
        id: boxesClone[boxID].id,
        items: boxesClone[boxID].items.map((item) => item.id),
      };
    });

    return {
      activeView,
      banner,
      clipartID,
      boxes: boxesClone,
      boxOrder,
      card: {
        ...card,
        position: defaultState.card.position,
        size: defaultState.card.size,
      },
    };
  }

  // Floating Action Button (+ symbol)
  function handleClickFAB() {
    let nextView = 'INSTRUCTIONS';

    if (activeView === 'INSTRUCTIONS' || (activeView === 'DESIGNER' && banner)) {
      nextView = 'DESIGNER';
      setIsProductsOverlayVisible(true);
    }
    setActiveView(nextView);
  }

  function handleClickCloseProductsOverlay() {
    const nextView = !banner ? 'INSTRUCTIONS' : 'DESIGNER';

    setIsProductsOverlayVisible(false);
    setActiveView(nextView);
  }

  function handleDragEnd(result) {
    const {source, destination, draggableId} = result;

    // Draggable was not dropped in droppable
    if (!destination) {
      return;
    }

    // Nothing changed
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Trashcan
    if (destination.droppableId === 'trashcan') {
      // Clipart
      if (source.droppableId === 'clipart') {
        setClipartID(null);
      } else if (source.droppableId === 'all-elkonin-boxes') {
        // Elkonin box
        const {[draggableId]: omit, ...rest} = boxes;
        const newBoxOrder = Array.from(boxOrder);
        newBoxOrder.splice(source.index, 1);

        setBoxes(rest);
        setBoxOrder(newBoxOrder);
      } else {
        // Char
        const sourceBox = boxes[source.droppableId];
        const newSourceBoxItems = Array.from(sourceBox.items);
        newSourceBoxItems.splice(source.index, 1);

        const newSourceBox = {
          ...sourceBox,
          items: newSourceBoxItems,
        };

        setBoxes({
          ...boxes,
          [newSourceBox.uuid]: newSourceBox,
        });
      }
      new Audio(trashSound).play();
      return;
    }

    if (source.droppableId === 'all-elkonin-boxes') {
      const newBoxOrder = Array.from(boxOrder);
      newBoxOrder.splice(source.index, 1);
      newBoxOrder.splice(destination.index, 0, draggableId);

      setBoxOrder(newBoxOrder);
      return;
    }

    const sourceBox = boxes[source.droppableId];
    const destinationBox = boxes[destination.droppableId];

    const newSourceBoxItems = Array.from(sourceBox.items);
    const [removed] = newSourceBoxItems.splice(source.index, 1);

    let newDestinationBoxItems;
    let newDestinationBox;
    // Moving within same box
    if (sourceBox.uuid === destinationBox.uuid) {
      newSourceBoxItems.splice(destination.index, 0, removed);
    } else {
      // Moving to a different box
      newDestinationBoxItems = Array.from(destinationBox.items);
      newDestinationBoxItems.splice(destination.index, 0, removed);

      newDestinationBox = {
        ...destinationBox,
        items: newDestinationBoxItems,
      };
    }

    const newSourceBox = {
      ...sourceBox,
      items: newSourceBoxItems,
    };

    setBoxes({
      ...boxes,
      [newSourceBox.uuid]: newSourceBox,
      ...(newDestinationBox && {
        [newDestinationBox.uuid]: newDestinationBox,
      }),
    });
  }

  function handleClickAddItem(id, model) {
    if (model.type === 'BANNER') {
      setBanner(id);
    } else if (model.type === 'CLIPART') {
      setClipartID(id);
    } else if (model.type === 'CARD') {
      setCard({
        ...card,
        id,
        ...(!card?.id && {
          // If card already placed, dont change position
          // Near the middle
          relativePosition: {
            x: 0.43,
            y: 0.5,
          },
          // 1/6 size of container
          relativeSize: {
            width: 0.16,
            height: 0.16,
          },
        }),
      });
    } else if (model.type === 'BOX') {
      const box = {
        ...model,
        uuid: uuidv4(),
        items: [],
      };

      setBoxes({
        ...boxes,
        [box.uuid]: box,
      });
      setBoxOrder([...boxOrder, box.uuid]);
    } else {
      const charBankItem = {
        ...model,
        uuid: uuidv4(),
      };

      setBoxes({
        ...boxes,
        [CHAR_BANK_ID]: {
          ...boxes[CHAR_BANK_ID],
          items: [...boxes[CHAR_BANK_ID].items, charBankItem],
        },
      });
    }
  }

  // Move char from one character bank to another
  function switchCharBank(model, from, to) {
    const newFromItems = boxes[from].items.filter((item) => item.uuid !== model.uuid);
    const newToItems = boxes[to].items.concat(model);

    setBoxes({
      [from]: {
        ...boxes[from],
        items: newFromItems,
      },
      [to]: {
        ...boxes[to],
        items: newToItems,
      },
    });
  }

  // Reset all back to saved config
  function handleReset() {
    setIsCardVisible(false);
    loadStateFromConfig();
  }

  // Clear everything
  function handleStartOver() {
    setActiveView(defaultState.activeView);
    setIsProductsOverlayVisible(false);
    setBanner(DEFAULT_WHITEBOARD);
    setClipartID(null);
    setBoxes(defaultState.boxes);
    setBoxOrder([]);
    setIsCardVisible(false);
    setCard(defaultState.card);
  }

  // Temporary card
  function handleClickCard() {
    setIsCardVisible(!isCardVisible);
  }

  // Card that is saved with exercise
  function handleClickCloseCard() {
    setCard(defaultState.card);
  }

  function calculateCardContainerOffsetPercents() {
    const container = document.getElementsByClassName('designer-banner-container')[0].getBoundingClientRect();
    const card = document.getElementsByClassName('react-draggable')[0].getBoundingClientRect();

    const xOffset = card.left - container.left;
    const xOffsetPercent = xOffset / container.width;

    const yOffset = card.top - container.top;
    const yOffsetPercent = yOffset / container.height;

    return {
      xOffsetPercent,
      yOffsetPercent,
      heightPercent: card.height / container.height,
      widthPercent: card.width / container.width,
    };
  }

  function onCardDragStop(position) {
    const cardContainerOffsetPercents = calculateCardContainerOffsetPercents();
    setCard({
      ...card,
      position,
      relativePosition: {
        x: cardContainerOffsetPercents.xOffsetPercent,
        y: cardContainerOffsetPercents.yOffsetPercent,
      },
      relativeSize: {
        height: cardContainerOffsetPercents.heightPercent,
        width: cardContainerOffsetPercents.widthPercent,
      },
    });
  }

  function onCardResizeStop(width, height, position) {
    const cardContainerOffsetPercents = calculateCardContainerOffsetPercents();
    setCard({
      ...card,
      size: {
        width,
        height,
      },
      position,
      relativePosition: {
        x: cardContainerOffsetPercents.xOffsetPercent,
        y: cardContainerOffsetPercents.yOffsetPercent,
      },
      relativeSize: {
        height: cardContainerOffsetPercents.heightPercent,
        width: cardContainerOffsetPercents.widthPercent,
      },
    });
  }

  function renderAppBody() {
    if (activeView === 'INSTRUCTIONS') {
      return <Instructions key="001" />;
    } else if (activeView === 'DESIGNER') {
      return (
        <Designer
          key="002"
          handleDragEnd={handleDragEnd}
          banner={banner}
          clipartID={clipartID}
          boxes={boxes}
          boxOrder={boxOrder}
          switchCharBank={switchCharBank}
          handleCardContainerResize={handleCardContainerResize}
        />
      );
    }
  }

  function renderDrawer() {
    return (
      <Drawer
        anchor="right"
        variant="persistent"
        open={isProductsOverlayVisible}
        onClose={handleClickCloseProductsOverlay}
      >
        <ProductsOverlay
          banner={banner}
          onClickActionBtn={handleClickAddItem}
          onClickClose={handleClickCloseProductsOverlay}
        />
      </Drawer>
    );
  }

  return (
    <>
      {mode === 'EXERCISE' && (
        <ExerciseAppBar exercise={exercise}>
          {isEditable && (
            <Button className={baseStyles['btn-pill']} color="inherit" onClick={handleClickSave}>
              SAVE THIS EXERCISE
            </Button>
          )}
        </ExerciseAppBar>
      )}

      <SecondaryAppBar
        onClickReset={handleReset}
        onClickClear={handleStartOver}
        onClickFAB={handleClickFAB}
        onClickCard={handleClickCard}
      />

      <div className="screen-width-warning-cover">
        <FlamingoLiteracyLogoDark style={{width: 250, height: 91}} />
        <h4 className="message title">This screen is too small!</h4>
        <h5 className="message subtitle">Please grab a tablet or something larger.</h5>
      </div>

      {renderAppBody()}
      {isCardVisible && <Card onClose={handleClickCard} />}

      {card?.id && card?.position?.x && (
        <CardControlled
          bounds=".designer-banner-container"
          position={card.position}
          size={card.size}
          color={shapes[card.id].colorHex}
          onCardDragStop={onCardDragStop}
          onCardResizeStop={onCardResizeStop}
          onClose={handleClickCloseCard}
        />
      )}

      {renderDrawer()}
    </>
  );
}
